// 3x3 cubie extraction: corners and edges with orientation.
// This is the bridge between the sticker model and cubie-level algorithms
// (validation, two-phase search, and parity detection).

import { U, D, R, L, F, B, applyMoves } from "./model.js";

// Corner slots in uniform reading order [U/D sticker, R/L sticker, F/B sticker].
// Cells are face*9 + row*3 + col.
export const CORNER_SLOTS = [
  [U * 9 + 2 * 3 + 2, R * 9 + 0, F * 9 + 2 * 3 + 0], // URF
  [U * 9 + 0 * 3 + 2, L * 9 + 2 * 3 + 0, F * 9 + 0], // UFL
  [U * 9 + 0 * 3 + 0, L * 9 + 0, B * 9 + 2 * 3 + 0], // ULB
  [U * 9 + 2 * 3 + 0, R * 9 + 2 * 3 + 0, B * 9 + 0], // UBR
  [D * 9 + 2 * 3 + 2, R * 9 + 0 * 3 + 2, F * 9 + 2 * 3 + 2], // DFR
  [D * 9 + 0 * 3 + 2, L * 9 + 2 * 3 + 2, F * 9 + 0 * 3 + 2], // DLF
  [D * 9 + 0 * 3 + 0, L * 9 + 0 * 3 + 2, B * 9 + 2 * 3 + 2], // DBL
  [D * 9 + 2 * 3 + 0, R * 9 + 2 * 3 + 2, B * 9 + 0 * 3 + 2], // DRB
];

// Edge slots: [primary cell, secondary cell]. Primary = the cell on U/D for
// U/D edges, on F/B for middle-slice edges.
export const EDGE_SLOTS = [
  [U * 9 + 2 * 3 + 1, R * 9 + 1 * 3 + 0], // UR
  [U * 9 + 1 * 3 + 2, F * 9 + 1 * 3 + 0], // UF
  [U * 9 + 0 * 3 + 1, L * 9 + 1 * 3 + 0], // UL
  [U * 9 + 1 * 3 + 0, B * 9 + 1 * 3 + 0], // UB
  [D * 9 + 2 * 3 + 1, R * 9 + 1 * 3 + 2], // DR
  [D * 9 + 1 * 3 + 2, F * 9 + 1 * 3 + 2], // DF
  [D * 9 + 0 * 3 + 1, L * 9 + 1 * 3 + 2], // DL
  [D * 9 + 1 * 3 + 0, B * 9 + 1 * 3 + 2], // DB
  [F * 9 + 2 * 3 + 1, R * 9 + 0 * 3 + 1], // FR
  [F * 9 + 0 * 3 + 1, L * 9 + 2 * 3 + 1], // FL
  [B * 9 + 0 * 3 + 1, R * 9 + 2 * 3 + 1], // BR: B(0,1), R(2,1)
  [B * 9 + 2 * 3 + 1, L * 9 + 0 * 3 + 1], // BL: B(2,1), L(0,1)
];

// Piece identity by sorted color set. Corners keyed "024" etc.
const CORNER_BY_COLORS = {
  "024": 0, // URF
  "034": 1, // UFL
  "035": 2, // ULB
  "025": 3, // UBR
  124: 4, // DFR
  134: 5, // DLF
  135: 6, // DBL
  125: 7, // DRB
};
const EDGE_BY_COLORS = {
  "02": 0, // UR
  "04": 1, // UF
  "03": 2, // UL
  "05": 3, // UB
  12: 4, // DR
  14: 5, // DF
  13: 6, // DL
  15: 7, // DB
  24: 8, // FR
  34: 9, // FL
  25: 10, // BR
  35: 11, // BL
};
export const CORNER_NAMES = [
  "URF",
  "UFL",
  "ULB",
  "UBR",
  "DFR",
  "DLF",
  "DBL",
  "DRB",
];
export const EDGE_NAMES = [
  "UR",
  "UF",
  "UL",
  "UB",
  "DR",
  "DF",
  "DL",
  "DB",
  "FR",
  "FL",
  "BR",
  "BL",
];

function colorsOf(state, cells) {
  const cols = cells.map((c) => (state[(c / 9) | 0][c % 9] / 9) | 0);
  return cols;
}

function sortKey(cols) {
  return [...cols].sort((a, b) => a - b).join("");
}

// Extract cubie state: { cp: Int8Array(8), co: Int8Array(8), ep: Int8Array(12), eo: Int8Array(12) }
// cp[i] = corner piece at slot i; co[i] = its orientation (0..2).
// ep[i] = edge piece at slot i; eo[i] = its orientation (0..1).
// Canonical right-handed normal triples per corner slot, starting from the
// corner's U/D-facing normal. Corner twist = index (0..2) of the U/D
// sticker's facing face in the slot's triple. This convention makes the
// mod-3 twist sum invariant under every move (verified by tests).
export const CORNER_CANON = [
  [U, F, R], // URF
  [U, L, F], // UFL
  [U, B, L], // ULB
  [U, R, B], // UBR
  [D, R, F], // DFR
  [D, F, L], // DLF
  [D, L, B], // DBL
  [D, B, R], // DRB
];

// Corner twist: index of the U/D sticker's facing face in the slot's
// canonical triple (0 = oriented).
export function extractCubies(state) {
  const cp = new Int8Array(8);
  const co = new Int8Array(8);
  for (let s = 0; s < 8; s++) {
    const cells = CORNER_SLOTS[s];
    const cols = colorsOf(state, cells);
    const piece = CORNER_BY_COLORS[sortKey(cols)];
    if (piece === undefined)
      throw new Error(`Bad corner colors ${cols} at slot ${s}`);
    cp[s] = piece;
    const canon = CORNER_CANON[s];
    for (let i = 0; i < 3; i++) {
      if (cols[i] === U || cols[i] === D) {
        const face = (cells[i] / 9) | 0;
        co[s] = canon.indexOf(face);
        break;
      }
    }
  }
  const ep = new Int8Array(12);
  const eo = new Int8Array(12);
  for (let s = 0; s < 12; s++) {
    const cells = EDGE_SLOTS[s];
    const cols = colorsOf(state, cells);
    const piece = EDGE_BY_COLORS[sortKey(cols)];
    if (piece === undefined)
      throw new Error(`Bad edge colors ${cols} at slot ${s}`);
    ep[s] = piece;
    // primary color: U/D color if the piece has one, else the F/B color
    const hasUD = cols[0] <= 1 || cols[1] <= 1;
    let primary = hasUD
      ? cols[0] <= 1
        ? cols[0]
        : cols[1]
      : cols[0] === F || cols[0] === B
        ? cols[0]
        : cols[1];
    eo[s] = cols[0] === primary ? 0 : 1;
  }
  return { cp, co, ep, eo };
}

// Permutation parity of an array mapping slots -> pieces (0 = even).
export function permutationParity(perm) {
  const n = perm.length;
  const seen = new Uint8Array(n);
  let parity = 0;
  for (let i = 0; i < n; i++) {
    if (seen[i]) continue;
    let cur = i;
    let len = 0;
    while (!seen[cur]) {
      seen[cur] = 1;
      len++;
      cur = perm[cur];
    }
    parity ^= (len - 1) & 1;
  }
  return parity;
}

// 3x3 legality: corner orientation, edge orientation, permutation parity.
export function cubieValid({ cp, co, ep, eo }) {
  let twist = 0;
  for (let i = 0; i < 8; i++) twist += co[i];
  if (twist % 3 !== 0)
    return { valid: false, reason: "corner orientation parity" };
  let flip = 0;
  for (let i = 0; i < 12; i++) flip += eo[i];
  if (flip % 2 !== 0)
    return { valid: false, reason: "edge orientation parity" };
  const cpParity = permutationParity(cp);
  const epParity = permutationParity(ep);
  if (cpParity !== epParity)
    return { valid: false, reason: "permutation parity" };
  return { valid: true, reason: "" };
}

// Rotate a cloned 3x3 state so its centers are canonical (U color on the U
// face, F color on the F face). Whole-cube rotations are legal moves, so the
// normalized state is legal iff the original is; the classical parity
// invariant (cpPar == epPar) only applies in this canonical orientation.
export function normalize3x3(state) {
  const area = 9;
  const rot = (face, times) => ({
    axis: [0, 1, 2][{ R: 0, U: 1, F: 2 }[face]],
    layers: [0, 1, 2],
    times,
    face,
    depth: 0,
    width: 3,
    turns: times,
  });
  const moves = [];
  let uFace = -1;
  for (let f = 0; f < 6; f++) if (((state[f][4] / area) | 0) === U) uFace = f;
  switch (uFace) {
    case 1:
      moves.push(rot("R", 2));
      break;
    case 2:
      moves.push(rot("F", 3));
      break; // z'
    case 3:
      moves.push(rot("F", 1));
      break; // z
    case 4:
      moves.push(rot("R", 1));
      break; // x
    case 5:
      moves.push(rot("R", 3));
      break; // x'
    default:
      break;
  }
  const s = moves.length ? applyMoves(state, 3, moves) : state;
  let fFace = -1;
  for (let f = 0; f < 6; f++) if (((s[f][4] / area) | 0) === F) fFace = f;
  switch (fFace) {
    case 2:
      moves.push(rot("U", 3));
      break; // y'
    case 3:
      moves.push(rot("U", 1));
      break; // y
    case 5:
      moves.push(rot("U", 2));
      break; // y2
    default:
      break;
  }
  return moves.length > 1 ? applyMoves(state, 3, moves) : s;
}
